import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { useBrandShellNode } from '@/components/ui/brand-shell'

const POLL_INTERVAL_MS = 60 * 1000 // 60s while tab is visible

/**
 * Detects when a new deploy has shipped and surfaces a "Refresh" prompt.
 *
 * Why this exists:
 * - Hashed Vite bundles + `index.html` no-cache + a destroyer service worker
 *   ensure that *new visits* see the latest code. But users with the tab
 *   already open won't pick up changes until they reload — and they won't
 *   know to reload unless something tells them.
 * - This component fetches `/version.json` (emitted at build time) periodically
 *   while the tab is visible, and on visibility change. When the served
 *   version differs from the one baked into the running bundle, it shows a
 *   small banner with a refresh button.
 *
 * Non-intrusive on purpose: the user keeps control of when to reload so
 * mid-flow input isn't lost. The banner stays until refreshed or dismissed.
 */
export function UpdateBanner() {
  const { t } = useTranslation()
  // Above every early return (check-hook-order); it picks the parent, not
  // whether the pill shows.
  const shell = useBrandShellNode()
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const checkingRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    let intervalId: ReturnType<typeof setInterval> | undefined

    const check = async () => {
      if (checkingRef.current || cancelled) return
      checkingRef.current = true
      try {
        const res = await fetch('/version.json', { cache: 'no-store' })
        if (!res.ok) return
        const data = (await res.json()) as { version?: string }
        if (cancelled) return
        if (data?.version && data.version !== __APP_VERSION__) {
          setUpdateAvailable(true)
        }
      } catch {
        // Network blip; try again on next tick
      } finally {
        checkingRef.current = false
      }
    }

    const startPolling = () => {
      if (intervalId) return
      intervalId = setInterval(() => {
        if (document.visibilityState === 'visible') check()
      }, POLL_INTERVAL_MS)
    }

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = undefined
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        check()
        startPolling()
      } else {
        stopPolling()
      }
    }

    // Run once on mount, then start polling if tab is visible
    if (document.visibilityState === 'visible') {
      check()
      startPolling()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      stopPolling()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  if (!updateAvailable || dismissed) return null

  const handleReload = () => {
    // Hard reload — bypass any HTTP cache the browser is still hanging on to.
    window.location.reload()
  }

  const pill = (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 -translate-x-1/2 z-[var(--z-toast)] flex items-center gap-3 px-4 py-2.5 rounded-full bg-[var(--action-primary-bg)] text-[var(--action-primary-text)] shadow-[var(--shadow-lg)] max-w-[calc(100vw-2rem)]"
      style={{
        // Same contract as InstallPrompt: mounted in App.tsx outside every
        // layout, so the one answer to «how much bottom edge is spoken for»
        // is --bottom-chrome-inset. Fixed `bottom-5` floated this pill ON TOP
        // of the mobile BottomTabBar (z-toast > z-sticky), where its
        // non-button body swallowed taps on the middle tab slots.
        bottom: 'calc(var(--bottom-chrome-inset, 0px) + max(20px, env(safe-area-inset-bottom, 20px)))',
      }}
    >
      <RefreshCw className="size-4 shrink-0" aria-hidden="true" />
      <span className="text-small font-medium">{t('common.updateAvailable')}</span>
      <button
        type="button"
        onClick={handleReload}
        /* `focus-ring` + a real target. Audited at 390px: this button measured
           34×19 and carried no focus indicator at all — and it is the only way
           to take the update the banner is announcing. `-my-2 py-2` grows the
           hit area without moving the text or the banner's height. */
        className="focus-ring -my-2 rounded-[var(--radius-sm)] px-1 py-2 text-small font-bold underline-offset-2 hover:underline cursor-pointer"
      >
        {t('common.refreshNow')}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('common.dismiss')}
        /* Three defects on one control, all measured: 5×19 hit area, 2.71:1
           contrast from the `/70` opacity, and no focus indicator. The dismiss
           on a toast is the one control a keyboard user most needs to reach.
           Full-opacity ink (the banner's own fill guarantees its contrast),
           a padded target, and the shared ring. */
        className="focus-ring -my-2 ml-1 rounded-[var(--radius-sm)] px-2 py-2 text-small text-[var(--action-primary-text)] cursor-pointer"
      >
        ×
      </button>
    </div>
  )

  // Same contract as InstallPrompt, and the same repair: into the brand-scoped
  // shell when one is mounted, so the pill wears the tenant's action colour
  // instead of our indigo. See `portal-container.tsx`.
  return shell ? createPortal(pill, shell) : pill
}
